import React, { Fragment, useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import { Skeleton, Typography, Box, Grid } from "@mui/material";
import { useRouter } from "next/router";
import { AnimatePresence } from "framer-motion";
import { useSnackbar } from "notistack";

import ProgressBar from "@common/ProgressBar";
import PageNotFoundContainer from "@common/PageNotFoundContainer";
import SelectVariantContainer from "@common/product/SelectVariantContainer";
import ProductDescriptionContainer from "@common/product/ProductDescriptionContainer";
import ProductQuestionsContainer from "@common/product/ProductQuestionsContainer";
import SelectQuantityContainer from "@common/product/SelectQuantityContainer";
import ProductMeta from "@config/meta/ProductMeta";

// import { validateVariantQuestion } from "@common/questions/helper/validation";
import { isDark } from "@common/utils/colors";
// import * as fbq from "@config/fbpixel";
import { checkProductAvailability } from "@common/utils/productUtils";
import { addItemToCart } from "@common/utils/cartUtils";
import useShopContext from "@hooks/useShopContext";
import Navbar from "@common/Navbar";

const PREFIX = "SelectedProduct";

const classes = {
  carteLabel: `${PREFIX}-carteLabel`,
  anchor: `${PREFIX}-anchor`,
};

const Root = styled("div")(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  // fontDisplay: "swap",
  backgroundColor: theme.palette.primary.main,

  [`& .${classes.carteLabel}`]: {
    textAlign: "center",
    width: "100%",
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    color: isDark(theme.palette.primary.main) ? "#FFF" : "#000",
  },

  [`& .${classes.anchor}`]: {
    textDecoration: "none",
    color: isDark(theme.palette.primary.main) ? "#FFF" : "#000",
    "&:hover": {
      color: "#FFD665",
    },
  },
}));

const SelectedProduct = () => {
  const router = useRouter();
  const { shop, products, loading } = useShopContext();

  const productId = router.query.productId;
  const product = products && products.filter((product) => product.product_id === productId)[0];

  const { enqueueSnackbar } = useSnackbar();

  const [pageNum, setPageNum] = useState("ProductDescription");
  const [responses, setResponses] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState();
  const [quantity, setQuantity] = useState(1);
  // eslint-disable-next-line no-unused-vars
  const [cartItems, setCartItems] = useState([]);

  const processProductObj = () => {
    if (selectedVariant) {
      let obj = { ...product };
      obj = {
        ...obj,
        product_id: selectedVariant.product_id,
        price: selectedVariant.price,
        variant_product_image: selectedVariant.product_image,
        variant_title: selectedVariant.title,
        variant_description: selectedVariant.description,
      };
      return obj;
    }
    return product;
  };

  const addToCarteHandler = () => {
    // add item to cart and redirect to shop home page
    const productObj = processProductObj();
    const productAvailable = checkProductAvailability(productObj);
    if (!productAvailable) {
      enqueueSnackbar("Sale of this product has ended!", { variant: "error" });
      return;
    }

    const result = addItemToCart(
      products,
      productObj,
      responses,
      quantity,
      retrieveUpdatedCartItems,
      enqueueSnackbar,
    );
    if (result) {
      enqueueSnackbar("Added to cart!", { variant: "success" });
      setTimeout(() => router.push("/products"), 500);
    }
  };

  const proceedToCheckoutHandler = () => {
    // add item to cart and redirect to cart summary page
    const productObj = processProductObj();
    const productAvailable = checkProductAvailability(productObj);
    if (!productAvailable) {
      enqueueSnackbar("Sale of this product has ended!", { variant: "error" });
      return;
    }

    const result = addItemToCart(
      products,
      productObj,
      responses,
      quantity,
      retrieveUpdatedCartItems,
      enqueueSnackbar,
    );
    if (result) {
      // fbq.initiateCheckout();
      router.push("/cart");
    }
  };

  const retrieveUpdatedCartItems = () => {
    if (localStorage.getItem("cart_items")) {
      setCartItems(JSON.parse(localStorage.getItem("cart_items")));
    } else {
      setCartItems([]);
    }
  };

  const renderContainers = () => {
    if (pageNum === "ProductDescription") {
      return (
        <ProductDescriptionContainer
          shop={shop}
          product={product}
          onClickFn={() => (product.use_variants ? setPageNum("SelectVariant") : setPageNum(0))} // skip variant page if not using variants
        />
      );
    } else if (pageNum === "SelectVariant") {
      return (
        <SelectVariantContainer
          product={product}
          setPageNum={setPageNum}
          onClickFn={() => setPageNum(0)}
          selectedVariant={selectedVariant}
          setSelectedVariant={setSelectedVariant}
        />
      );
    } else if (pageNum > -1 && pageNum <= product.product_questions.length - 1) {
      return (
        <ProductQuestionsContainer
          product={product}
          pageNum={pageNum}
          setPageNum={setPageNum}
          responses={responses}
          setResponses={setResponses}
          selectedVariant={selectedVariant}
        />
      );
    } else if (pageNum > -1 && pageNum === product.product_questions.length) {
      return (
        <SelectQuantityContainer
          pageNum={pageNum}
          setPageNum={setPageNum}
          product={product}
          quantity={quantity}
          setQuantity={setQuantity}
          addToCarte={addToCarteHandler}
          proceedToCheckout={proceedToCheckoutHandler}
          selectedVariant={selectedVariant}
        />
      );
    } else {
      return null;
    }
  };

  useEffect(() => {
    if (product) {
      setResponses(product.product_questions);
    }
    retrieveUpdatedCartItems();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // scroll to top of page on each page transition
  useEffect(() => window.scrollTo(0, 0), [pageNum]);

  // useEffect(() => {
  //   fbq.pageView();
  //   fbq.viewContent();

  //   const handleRouteChange = () => {
  //     fbq.pageView();
  //     fbq.viewContent();
  //   };

  //   router.events.on("routeChangeComplete", handleRouteChange);
  //   return () => {
  //     router.events.off("routeChangeComplete", handleRouteChange);
  //   };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [router.events]);

  if (loading) {
    return (
      <Root>
        {/* Navbar Skeleton */}
        {/* <Skeleton variant="rectangular" width="100%" height={64} animation="wave" /> */}
        <Navbar onBack={() => router.push("/products")} />
        <Box
          sx={{ maxWidth: 1280, margin: "auto", width: "100%", p: { xs: 2, md: 5 }, flexGrow: 1 }}
        >
          <Grid container spacing={{ xs: 2, md: 4 }}>
            {/* Image Skeleton */}
            <Grid item xs={12} md={6}>
              <Skeleton
                variant="rectangular"
                width="100%"
                sx={{ height: { xs: 300, sm: 400, md: 550 } }}
                animation="wave"
              />
            </Grid>
            {/* Description Skeleton */}
            <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column" }}>
              <Skeleton
                variant="text"
                sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, width: "80%" }}
                animation="wave"
              />{" "}
              {/* Title */}
              <Skeleton
                variant="text"
                sx={{ fontSize: { xs: "1rem", md: "1.25rem" }, width: "40%", mt: 1 }}
                animation="wave"
              />{" "}
              {/* Price */}
              <Skeleton
                variant="text"
                sx={{ fontSize: "0.875rem", width: "25%", mt: 1 }}
                animation="wave"
              />{" "}
              {/* Inventory */}
              <Skeleton variant="text" sx={{ mt: 3 }} animation="wave" />
              <Skeleton variant="text" animation="wave" />
              <Skeleton variant="text" animation="wave" />
              <Skeleton variant="text" width="90%" animation="wave" /> {/* Description */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: { xs: 3, md: 7 } }}>
                <Skeleton variant="rectangular" width={150} height={40} animation="wave" />{" "}
                {/* Button */}
              </Box>
            </Grid>
          </Grid>
        </Box>
        {/* Footer Skeleton */}
        <Box sx={{ textAlign: "center", width: "100%", py: 2, mt: "auto" }}>
          <Skeleton variant="text" width={150} sx={{ margin: "auto" }} animation="wave" />
        </Box>
      </Root>
    );
  }

  if (!product) {
    return <PageNotFoundContainer />;
  }

  return (
    <Fragment>
      <ProductMeta shop={shop} product={product} />
      <Root>
        {(pageNum > -1 ||
          pageNum === "ProductDescription" ||
          (pageNum === "SelectVariant" && product && product.use_variants)) && (
          <ProgressBar
            page={pageNum}
            total={product && product.product_questions.length + 1}
            section="beforecheckout"
          />
        )}

        <AnimatePresence exitBeforeEnter>{renderContainers()}</AnimatePresence>

        <div className={classes.carteLabel}>
          <Typography variant="body1">
            Powered by{" "}
            <a
              className={classes.anchor}
              href="https://carteapp.io/"
              target="_blank"
              rel="noreferrer"
            >
              Carte
            </a>
          </Typography>
        </div>
      </Root>
    </Fragment>
  );
};

export default SelectedProduct;
